import { getApiBaseUrl } from './apiConfig';
import previewStyles from '../components/PdfGenerationStudio.css?raw';

const API_BASE_URL = getApiBaseUrl();

const getPreviewThemeSnapshot = (element) => {
  const themeNode = element?.closest?.('.template-preview-theme')

  if (!themeNode) {
    return {
      densityClass: 'template-density-comfortable',
      fontScale: '1'
    }
  }

  const densityClass = Array.from(themeNode.classList).find((className) => className.startsWith('template-density-')) || 'template-density-comfortable'
  const computed = window.getComputedStyle(themeNode)
  const fontScale = computed.getPropertyValue('--template-font-scale').trim() || '1'

  return {
    densityClass,
    fontScale
  }
}

const toDataUrl = async (src) => {
  if (src.startsWith('data:')) return src

  const response = await fetch(src, { cache: 'force-cache' })
  if (!response.ok) {
    throw new Error(`Failed to inline image ${src}: ${response.status}`)
  }

  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error(`Failed to read image ${src}`))
    reader.readAsDataURL(blob)
  })
}

const inlineImages = async (node) => {
  const images = Array.from(node.querySelectorAll('img'))

  await Promise.all(images.map(async (img) => {
    try {
      img.setAttribute('src', await toDataUrl(img.src))
    } catch (_error) {
      // Leave the original src; the render service will try to load it itself.
    }
  }))
}

const buildPrintableHtml = (sheetNode, previewTheme) => {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base href="${window.location.origin}/" />
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;500;700&family=Sarabun:wght@400;500;700&display=swap" rel="stylesheet" />
    <style>
      ${previewStyles}

      /* Base reset + print adjustments only — component styles come from
         PdfGenerationStudio.css so preview and PDF share one source of truth. */
      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #111827;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      *, *::before, *::after {
        box-sizing: border-box;
      }
      .template-preview-export-shell {
        --template-font-scale: ${previewTheme.fontScale};
        width: 100%;
      }
      .preview-card,
      .preview-card-document {
        width: auto;
        margin: 0;
        background: #ffffff;
        border: none !important;
        box-shadow: none !important;
      }
      @media (max-width: 1024px) {
        .template-sheet {
          width: 210mm;
          min-height: 297mm;
          padding: 12mm 10mm;
        }
        .invoice-top-grid,
        .dual-box-grid,
        .po-signatures {
          grid-template-columns: 1fr 1fr;
        }
        .doc-header-logo {
          position: absolute;
          top: 0;
          right: 0;
          margin-top: 0;
        }
        .signature-block,
        .po-signatures,
        .quotation-signature {
          margin-top: auto;
          padding-top: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <div class="template-preview-theme ${previewTheme.densityClass} template-preview-export-shell">
      ${sheetNode.outerHTML}
    </div>
  </body>
</html>`
}

export async function exportTemplatePreviewPdf(element, fileName = 'preview.pdf') {
  if (!element) {
    throw new Error('Preview element not found')
  }

  const sourceSheet = (element.querySelector('.template-sheet') || element).cloneNode(true)
  await inlineImages(sourceSheet)
  const previewTheme = getPreviewThemeSnapshot(element)
  const html = buildPrintableHtml(sourceSheet, previewTheme)
  const token = localStorage.getItem('token')
  const safeFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`

  const response = await fetch(`${API_BASE_URL}/preview-pdf/render`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      html,
      fileName: safeFileName
    })
  })

  if (!response.ok) {
    const fallbackMessage = `Preview PDF render failed with status ${response.status}`
    let detailedMessage = fallbackMessage
    try {
      const errorBody = await response.json()
      detailedMessage = errorBody?.error
        ? `${errorBody?.message || fallbackMessage}: ${errorBody.error}`
        : (errorBody?.message || fallbackMessage)
    } catch (_error) {
      // ignore parse errors and use fallback message
    }
    throw new Error(detailedMessage)
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = safeFileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
